import React, { useState } from 'react';
import { X, User, Mail, Phone, Building, Globe, Loader2, AlertCircle, Shield, ShoppingBag, UserCircle } from 'lucide-react';
import Button from '../ui/Button';
import { AdminUserRecord, SupportedLocale } from '../../types';
import { adminApi } from '../../services/api';
import { SUPPORTED_LOCALES, useLocale } from '../../context/LocaleContext';

interface EditUserModalProps {
  user: AdminUserRecord;
  onClose: () => void;
  onSuccess: (user: AdminUserRecord) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  language: SupportedLocale;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { formatDate } = useLocale();
  const [formData, setFormData] = useState<FormData>({
    firstName: user.personalInfo?.firstName || '',
    lastName: user.personalInfo?.lastName || '',
    email: user.personalInfo?.email || '',
    phone: '',
    status: user.status || 'active',
    language: user.personalInfo?.language || 'en',
  });

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Invalid email format');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      await adminApi.updateUser(user.userId, {
        personalInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          language: formData.language,
        },
        status: formData.status,
      });

      // Return the updated user data
      const updatedUser: AdminUserRecord = {
        ...user,
        personalInfo: {
          ...user.personalInfo,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          language: formData.language,
        },
        status: formData.status,
      };

      onSuccess(updatedUser);
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeIcon = (type: string) => {
    switch (type) {
      case 'super_admin':
        return <Shield className="w-5 h-5 text-purple-500" />;
      case 'admin':
        return <UserCircle className="w-5 h-5 text-blue-500" />;
      case 'merchant':
        return <ShoppingBag className="w-5 h-5 text-green-500" />;
      default:
        return <UserCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getUserTypeBadge = (type: string) => {
    const styles: Record<string, string> = {
      super_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      merchant: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };
    return styles[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
      suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    return styles[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm h-screen">
      <div className="bg-white dark:bg-surface w-full max-w-lg rounded-xl shadow-2xl flex flex-col max-h-[90vh] border dark:border-border-color border-light-border-color animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="p-6 border-b dark:border-border-color border-light-border-color flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-primary/10 rounded-xl">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Edit User
                </h2>
                <p className="text-sm dark:text-text-secondary text-gray-500">
                  Update user information
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          
          {/* User Info Card */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border dark:border-gray-700 border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                {getUserTypeIcon(user.type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${getUserTypeBadge(user.type)}`}>
                    {user.type.replace('_', ' ')}
                  </span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(user.status)}`}>
                    {user.status}
                  </span>
                </div>
                <p className="text-sm dark:text-gray-400 text-gray-600 mt-1 flex items-center gap-1.5">
                  <Building className="w-3.5 h-3.5" />
                  {user.tenantName}
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Doe"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => updateField('status', e.target.value as FormData['status'])}
                className="w-full px-3 py-2.5 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Language/Locale */}
            <div>
              <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1.5">
                Language & Region
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={formData.language}
                  onChange={(e) => updateField('language', e.target.value as SupportedLocale)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors appearance-none"
                >
                  {SUPPORTED_LOCALES.map((locale) => (
                    <option key={locale.code} value={locale.code}>
                      {locale.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Affects date and currency formatting
              </p>
            </div>
          </div>

          {/* Read-only Info */}
          <div className="pt-4 border-t dark:border-gray-700 border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 bg-primary rounded-full"></div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">Account Information</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border dark:border-gray-700 border-gray-200">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">User ID</p>
                <p className="font-mono text-xs dark:text-gray-200 text-gray-800 break-all leading-relaxed">{user.userId}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border dark:border-gray-700 border-gray-200">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Tenant ID</p>
                <p className="font-mono text-xs dark:text-gray-200 text-gray-800 break-all leading-relaxed">{user.tenantId}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border dark:border-gray-700 border-gray-200">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Created</p>
                <p className="text-sm font-medium dark:text-gray-200 text-gray-800">
                  {user.createdAt ? formatDate(user.createdAt, { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  }) : 'N/A'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border dark:border-gray-700 border-gray-200">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Last Updated</p>
                <p className="text-sm font-medium dark:text-gray-200 text-gray-800">
                  {user.updatedAt ? formatDate(user.updatedAt, { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t dark:border-border-color border-light-border-color bg-gray-50 dark:bg-gray-800/30">
          <div className="flex gap-3">
            <Button 
              variant="secondary" 
              onClick={onClose} 
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              className="flex-1"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
